import { useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import {
  FeatureSchema,
  featureGet,
  featurePut,
} from "../../api/vehicleFeatures";

// Stale saves fail instead of overwriting definitions from another tab.
export function useFeatureSchema(workspaceId: string) {
  const [schema, setSchema] = useState<FeatureSchema>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const generation = useRef(0);
  const saving = useRef(false);
  useEffect(() => {
    const request = ++generation.current;
    setSchema(undefined);
    setError(undefined);
    featureGet<FeatureSchema>(workspaceId, "schema")
      .then((value) => {
        if (generation.current === request) setSchema(value);
      })
      .catch(() => {
        if (generation.current === request) setError("features.error");
      });
    return () => {
      generation.current = request + 1;
    };
  }, [workspaceId, reloadKey]);
  const save = async (next: FeatureSchema) => {
    if (!schema || saving.current) throw new Error("Settings are not ready");
    const request = generation.current;
    saving.current = true;
    setBusy(true);
    setError(undefined);
    try {
      const result = await featurePut<FeatureSchema>(workspaceId, "schema", {
        expectedRevision: schema.revision,
        definitions: next.definitions,
        enabled: next.enabled,
      });
      if (generation.current === request) setSchema(result);
    } catch (failure) {
      const code = isAxiosError(failure)
        ? failure.response?.data?.error
        : undefined;
      if (generation.current === request)
        setError(
          code === "vehicle_feature_in_use"
            ? "features.inUse"
            : code === "vehicle_feature_usage_limit"
              ? "features.usageLimit"
              : "features.error",
        );
      throw failure;
    } finally {
      saving.current = false;
      if (generation.current === request) setBusy(false);
    }
  };
  return {
    schema,
    error,
    busy,
    save,
    clearError: () => setError(undefined),
    reload: () => setReloadKey((v) => v + 1),
  };
}
